import { Stack, TextField, Typography, FormControl, FormControlLabel, RadioGroup, Radio, FormHelperText, Switch } from '@mui/material'
import SmartSelect from '../../../generic/smartSelect/SmartSelect.jsx'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { CheckCorrectTimeFormat } from '../../../generic/DateTimeFunctions.jsx'
import { DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers'

class BaseWidgetProvider {
  getCode () {
    throw new Error('Method getCode must be implemented')
  }

  getName (globalData) {
    throw new Error('Method getName must be implemented')
  }

  getIcon (globalData) {
    return QuestionMarkIcon
  }

  getStatus (globalData) {
    return null
  }

  /**
   * @param {object} props Properties object
   * @param {number} props.id Id value
   */
  createWidget (props) {
    throw new Error('Method createWidget must be implemented')
  }

  /**
   * @param {object} props Properties object
   * @param {number} props.id Id value
   */
  createPreviewWidget (props) {
    if (this.createWidget) {
      return this.createWidget(props)
    }
    throw new Error('Method createPreviewWidget must be implemented')
  }

  isClickable (props) {
    return false
  }

  onClick () {

  }

  getCustomHeader (props) {
    return null
  }

  getWidgetInfo (columns, cellHeight, size = null) {
    return {
      minW: size?.minW ?? 1,
      minH: size?.minH ?? 1,
      defaultW: size?.defaultW ?? 1,
      defaultH: size?.defaultH ?? 1,
      maxW: size?.maxW ?? undefined,
      maxH: size?.maxH ?? undefined,
      lazyLoad: true,
      availableWithoutDevice: false,
      hideHeader: false
    }
  }

  getConfigurationInfo () {
    return null
  }

  getDefaultConfiguration () {
    const configurationInfo = this.getConfigurationInfo()
    if (!configurationInfo) {
      return null
    }
    const data = configurationInfo ? {} : null
    if (configurationInfo) {
      configurationInfo.forEach((conf) => { data[conf.key] = conf.defaultValue })
    }
    return data
  }

  createConfigurationWidget (data, setData, t_) {
    const defaultConfig = this.getConfigurationInfo()
    if (defaultConfig) {
      return this.#createDefaultWidgetConfigurationRender(defaultConfig, data, setData, t_)
    }
    return null
  }

  #createDefaultWidgetConfigurationRender (configInfo, data, setData, t_) {
    const updateValue = (key, value) => {
      const newData = data ? { ...data } : {}
      newData[key] = value
      setData(newData)
    }

    const updateTimeRangeValue = (key, value) => {
      const newData = data ? { ...data } : {}
      newData[key] = value
      newData[`${key}Error`] = CheckCorrectTimeFormat(value)
      setData(newData)
    }
    const getDateTimePicker = (key, label, resolvedItemValue) => {
      return (
        <DateTimePicker
          key={key}
          label={t_(label)}
          value={resolvedItemValue}
          onChange={(newValue) => {
            updateValue(key, newValue)
          }}
          disableFuture
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock
          }}
        />
      )
    }

    return (
      <Stack sx={{ padding: '1rem', paddingTop: 0, backgroundColor: 'background.paper' }}>
        <h3>{t_('Configuration')}</h3>
        <Stack gap={3}>
          {configInfo.map((item) => {
            if (item.type === 'timeRange') {
              const mode = data?.timeRangeMode ?? 'last'
              const currentItemValue = data?.[item.key]
              const resolvedItemValue = (currentItemValue === undefined || currentItemValue === null || currentItemValue === '')
                ? (item.defaultValue ?? '')
                : data?.timeRangeMode === 'range' ? new Date(currentItemValue) : currentItemValue
              const updateTimeRangeMode = (modeValue) => {
                const newData = data ? { ...data } : {}
                newData.timeRangeMode = modeValue
                if (modeValue === 'range') {
                  const startDefault = configInfo.find((c) => c.key === 'startTime')?.defaultValue
                  const endDefault = configInfo.find((c) => c.key === 'endTime')?.defaultValue
                  if (!newData.startTime) newData.startTime = startDefault || ''
                  if (!newData.endTime) newData.endTime = endDefault || ''
                }
                setData(newData)
              }
              if (item.key === 'time') {
                return (
                  <div key={item.key}>
                    <Typography gutterBottom>{t_('Time range')}</Typography>
                    <FormControl component='fieldset' sx={{ mb: 1, width: '100%' }}>
                      <RadioGroup
                        value={mode}
                        onChange={(event) => {
                          updateTimeRangeMode(event.target.value)
                        }}
                      >
                        <FormControlLabel value='last' control={<Radio size='small' />} label={t_('Last')} />
                        <FormControlLabel value='range' control={<Radio size='small' />} label={t_('Range')} />
                      </RadioGroup>
                      {mode === 'range'
                        ? (
                          <FormHelperText>{t_('Select start and end date/time for the range')}</FormHelperText>
                          )
                        : <FormHelperText>{t_('Time to show from now')}</FormHelperText>}
                    </FormControl>
                    {mode === 'last' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography>{t_('Last')}</Typography>
                        <TextField
                          size='small'
                          placeholder='hh:mm:ss'
                          value={resolvedItemValue}
                          sx={{ width: 140 }}
                          onChange={(event) => {
                            const newValue = event.target.value
                            updateTimeRangeValue(item.key, newValue)
                          }}
                          error={!!data?.[`${item.key}Error`]}
                          helperText={data?.[`${item.key}Error`] ? data[`${item.key}Error`] : ''}
                        />
                      </div>
                    )}
                  </div>
                )
              }
              if (item.key === 'startTime' && mode === 'range') {
                return (
                  getDateTimePicker(item.key, 'From', resolvedItemValue)
                )
              }
              if (item.key === 'endTime' && mode === 'range') {
                return (
                  getDateTimePicker(item.key, 'To', resolvedItemValue)
                )
              }
              return null
            }
            if (item.type === 'boolean') {
              const checked = data?.[item.key] ?? item.defaultValue ?? false
              return (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Switch
                      checked={!!checked}
                      onChange={(event) => {
                        updateValue(item.key, event.target.checked)
                      }}
                    />
                  }
                  label={t_(item.label)}
                />
              )
            }
            if (item.type === 'select') {
              return (
                <SmartSelect
                  key={item.key}
                  selectableOptions={item.selectableOptions}
                  label={item.label}
                  value={data[item.key]}
                  onChange={(newValue) => {
                    updateValue(item.key, newValue)
                  }}
                />
              )
            }

            return (
              <TextField
                key={item.key}
                value={data ? data[item.key] : ''}
                label={item.label}
                type={item.type}
                inputProps={item.inputProps}
                onChange={(e) => {
                  const val = e.target.value
                  if (item.type === 'number' && item.inputProps?.min !== undefined) {
                    const min = Number(item.inputProps.min)
                    if (min >= 0 && val.includes('-')) return
                    if (min >= 1 && val === '0') return
                  }
                  updateValue(item.key, val)
                }}
                onKeyDown={(e) => {
                  if (item.type === 'number' && item.inputProps?.min >= 0 && e.key === '-') {
                    e.preventDefault()
                  }
                }}
              />
            )
          })}
        </Stack>
      </Stack>
    )
  }
}

export default BaseWidgetProvider
